import { NFIdentity } from '@nibras/core/common';
import { cloneDeep } from 'lodash';
import { BehaviorSubject, combineLatest, filter, map, Observable, Subject } from 'rxjs';
import { SharedCommonService } from '../shared-common.service';
import { App, LinkedApp } from './app.model';
import { HierarchyTagWrapper } from './common.interfaces';
import { CreateInstanceParam } from './create-instance-param.model';
import { CreateTaskEventArgs } from './create-task-event-args.model';
import { dcGroupDetails } from './dc-group-details.model';
import { dcTagFlatTree } from './dc-tag-flat-tree.model';
import { dcTag } from './dcTag.model';
import { OneTimeValue } from './one-time-value.model';
import { dcExternalParameter } from './request/request.model';

export class AppCache<
  TAppConfig extends App = App,
  TAppRouteParams extends AppRouteStateParams = AppRouteStateParams
> {
  widgetId?: number;
  activityID?: number;
  processAppId?: number;
  processName?: string;
  appConfig?: TAppConfig;

  shouldWaitForHiearchy = new BehaviorSubject<boolean>(null);
  linkedApps: AppCache<App, AppRouteStateParams>[];
  public get isHiearchyChangeInProgress(): boolean {
    return this.shouldWaitForHiearchy.value;
  }
  public set isHiearchyChangeInProgress(value: boolean) {
    this.shouldWaitForHiearchy.next(value);
  }

  public get hierarchyData(): HierarchyTagWrapper {
    return this.hierarchy.value;
  }

  elementBrowserData: AppElementBrowserData;
  lastAccessedOn: Date;
  processParams: ProcessParams;
  appRouteStateParams = new OneTimeValue<TAppRouteParams>(null);
  isPreview: boolean;
  isReadOnly = false;

  public tagsToTriggerAfterLoad: SidebarTags;
  public previousAppProfileTagList: dcTag[];

  private isAuthorized: BehaviorSubject<boolean>;
  public isAuthorized$?: Observable<boolean>;

  private sidebarTabSelection: BehaviorSubject<SidebarTabAction>;
  public sidebarTabSelection$?: Observable<SidebarTabAction>;

  private sidebarTags: BehaviorSubject<SidebarTags>;
  public sidebarTags$?: Observable<SidebarTags>;

  private nestedSidebarTags: BehaviorSubject<SidebarTags>;
  public nestedSidebarTags$: Observable<SidebarTags>;

  private hierarchy: BehaviorSubject<HierarchyTagWrapper>;
  public hierarchy$: Observable<HierarchyTagWrapper>;

  private highlightElementTag: BehaviorSubject<dcTag>;
  public highlightElementTag$: Observable<dcTag>;

  private selectedScopeTag: BehaviorSubject<dcTag>;
  public selectedScopeTag$: Observable<dcTag>;

  private callFromHomePage?: Subject<dcTag>;
  public callFromHomePage$: Observable<dcTag>;

  public appSelectionChange?: Subject<AppSelectionParams>;
  public appSelectionChange$: Observable<AppSelectionParams>;

  public onDefaultAppActionChange?: BehaviorSubject<DefaultAppAction>;
  public onDefaultAppActionChange$: Observable<DefaultAppAction>;

  public onAppPreviewMode?: Subject<AppCache>;
  public onAppPreviewMode$: Observable<AppCache>;

  public selectedAppProgress?: BehaviorSubject<CommonProgressStatus>;
  public selectedAppProgress$: Observable<CommonProgressStatus>;

  public isOnLandingPage = true;
  public elementSelectorSelectedTagNode: dcTagFlatTree;
  public sidebarTabAction: SidebarTabAction;
  public topologyHierachyConfirmFn: () => boolean;

  private onWidgetDateChange: BehaviorSubject<dcExternalParameter>;
  public widgetDate$?: Observable<dcExternalParameter>;

  private onCreateTask: BehaviorSubject<CreateTaskEventArgs>;
  public task$?: Observable<CreateTaskEventArgs>;

  constructor(public appId: number) {
    this.highlightElementTag = new BehaviorSubject(null);
    this.selectedScopeTag = new BehaviorSubject(null);
    this.sidebarTags = new BehaviorSubject(null);
    this.nestedSidebarTags = new BehaviorSubject(null);
    this.hierarchy = new BehaviorSubject(null);
    this.onDefaultAppActionChange = new BehaviorSubject<DefaultAppAction>(null);
    this.callFromHomePage = new Subject();
    this.isAuthorized = new BehaviorSubject(null);
    this.onAppPreviewMode = new Subject();
    this.sidebarTabSelection = new BehaviorSubject<SidebarTabAction>(null);

    this.sidebarTags$ = this.sidebarTags.asObservable();
    this.nestedSidebarTags$ = this.nestedSidebarTags.asObservable();
    this.hierarchy$ = this.hierarchy.asObservable();
    this.highlightElementTag$ = this.highlightElementTag.asObservable();
    this.selectedScopeTag$ = this.selectedScopeTag.asObservable();
    this.callFromHomePage$ = this.callFromHomePage.asObservable();
    this.onDefaultAppActionChange$ = this.onDefaultAppActionChange.asObservable();
    this.isAuthorized$ = this.isAuthorized.asObservable();
    this.onAppPreviewMode$ = this.onAppPreviewMode.asObservable();
    this.sidebarTabSelection$ = this.sidebarTabSelection.asObservable();

    this.appSelectionChange = new Subject();
    this.appSelectionChange$ = this.appSelectionChange.asObservable();

    this.selectedAppProgress = new BehaviorSubject(null);
    this.selectedAppProgress$ = this.selectedAppProgress.asObservable();
    this.onWidgetDateChange = new BehaviorSubject<dcExternalParameter>(null);
    this.widgetDate$ = this.onWidgetDateChange.asObservable();

    this.onCreateTask = new BehaviorSubject<CreateTaskEventArgs>(null);
    this.task$ = this.onCreateTask.asObservable();
  }

  public getSelectedAppProgress(linkedAppId?: number) {
    if (linkedAppId && this.linkedApps?.length) {
      const linkedApp = this.linkedApps.find(linkApp => linkApp.appId === linkedAppId);
      if (linkedApp) {
        return linkedApp.selectedAppProgress$;
      }
    }
    return this.selectedAppProgress$;
  }

  public toggleAppProgress(progressStatus: CommonProgressStatus) {
    this.selectedAppProgress.next(progressStatus);
  }

  public onSidebarTagsChanges(isNestedApp = false) {
    return this.getTags$(this.onSidebarTagsObjChanges(isNestedApp));
  }

  public onSidebarTagsObjChanges(isNestedApp = false) {
    return isNestedApp ? this.nestedSidebarTags$ : this.sidebarTags$;
  }

  private getTags$(tag$: Observable<SidebarTags>) {
    return (
      tag$ &&
      tag$.pipe(
        filter(a => a != null),
        map(a => a.tags)
      )
    );
  }

  public getSidebarTagsValue(isNestedApp = false) {
    return isNestedApp
      ? this.nestedSidebarTags.value && this.nestedSidebarTags.value.tags
      : this.sidebarTags.value && this.sidebarTags.value.tags;
  }

  public dispatchSidebarTags(
    tags: dcTag[],
    forceRefresh = false,
    dispatchToNestedApp = false,
    isDefaultProfileTag = false,
    appTagTrigger = AppTagTrigger.Default
  ) {
    const tag$ = dispatchToNestedApp ? this.nestedSidebarTags : this.sidebarTags;
    const existingTags = tag$.value && tag$.value.tags;
    let isSame = true;

    if (!forceRefresh) {
      if (existingTags && existingTags.length && tags) {
        if (existingTags.length != tags.length) {
          isSame = false;
        } else {
          existingTags.some(tag => {
            isSame = tags.some(
              inTags =>
                inTags.ObjectID === tag.ObjectID &&
                inTags.TagName === tag.TagName &&
                inTags.ElementTypeID === tag.ElementTypeID
            );

            if (!isSame) {
              return true;
            }
            return false;
          });
        }
      } else {
        isSame = false;
      }

      if (!isSame) {
        this.triggerTags(tags, tag$, isDefaultProfileTag, appTagTrigger);
      }
    } else {
      this.triggerTags(tags, tag$, isDefaultProfileTag, appTagTrigger);
    }
  }

  public isStaticApp() {
    // to exclude APP that support base element or template
    // eg:  Data Exp and Temp Exp
    return (
      this.appConfig &&
      !this.appConfig.GRID_MODE &&
      !this.appConfig.IsElementSelectionRequired &&
      this.appConfig.SupportedType
    );
  }

  public changeAppHierarchy(hierarchy: HierarchyTagWrapper, forceTags = false) {
    let triggerChangeEvent = true;

    if (!forceTags) {
      const previousHeirachyTag =
        this.hierarchyData?.profileTagList?.length &&
        this.hierarchyData.profileTagList[this.hierarchyData.profileTagList.length - 1];

      const lastTag = hierarchy.selectedHierarchyTag;

      if (lastTag && previousHeirachyTag && hierarchy.selectedHierarchy?.HierarchyCategoryID) {
        triggerChangeEvent = !(
          lastTag.CategoryID === previousHeirachyTag.CategoryID &&
          lastTag.TagName === previousHeirachyTag.TagName &&
          lastTag.ElementTypeID === previousHeirachyTag.ElementTypeID
        );
      }
    }

    if (triggerChangeEvent) {
      this.isHiearchyChangeInProgress = this.appConfig?.IsElementSelectionRequired;
      this.hierarchy.next(hierarchy);
    }
  }

  public reloadAppHierarchy(previousAppCache?: AppCache) {
    this.changeAppHierarchy(
      cloneDeep(previousAppCache?.hierarchyData || this.hierarchy.value),
      true
    );
  }

  public onHierarchyChanges() {
    return this.hierarchy$;
  }

  public isBaseElementApp() {
    return [NFIdentity.Element, NFIdentity.ElementTemplate].includes(this.appConfig.SupportedType);
  }

  private triggerTags(
    tags: dcTag[],
    tag$: BehaviorSubject<SidebarTags>,
    isDefaultProfileTag = false,
    appTagTrigger = AppTagTrigger.Default
  ) {
    let trigger = true;
    if (
      tags &&
      this.appConfig &&
      !this.appConfig.Is_Group &&
      this.appConfig.SupportedType &&
      !this.isStaticApp()
    ) {
      trigger = this.isBaseElementApp()
        ? true
        : SharedCommonService.canTriggerForSingleElementApp(tags, this.appConfig);
    }
    tag$.next(new SidebarTags(trigger ? tags : null, isDefaultProfileTag, appTagTrigger));
  }

  public setTriggerTagsAfterLoad(
    tags: dcTag[],
    previousAppProfileTagList?: dcTag[],
    appTagTrigger = AppTagTrigger.Default
  ) {
    this.tagsToTriggerAfterLoad = new SidebarTags(Object.assign([], tags), false, appTagTrigger);
    if (previousAppProfileTagList && previousAppProfileTagList.length) {
      this.previousAppProfileTagList = [
        Object.assign({}, previousAppProfileTagList[previousAppProfileTagList.length - 1])
      ];
    }
  }

  public dispatchHighlightElementTag(tag: dcTag) {
    this.highlightElementTag.next(tag);
  }

  public onHighlightElementTagChange() {
    return this.highlightElementTag$;
  }

  public dispatchSelectedScopeTag(tag: dcTag) {
    this.selectedScopeTag.next(tag);
  }

  public onSelectedScopeTagChange() {
    return this.selectedScopeTag$;
  }

  public dispatchTagFromHomePage(tag: dcTag) {
    this.callFromHomePage.next(tag);
  }

  public onTagFromHomePageChange() {
    return this.callFromHomePage$;
  }

  public setAuthorization(isAuthorized: boolean) {
    this.isAuthorized.next(isAuthorized);
  }

  public isAppAuthorized() {
    return (
      !!this.processParams?.ProcessID || //Assume app is authorized if its workflow app.
      this.isAuthorized.getValue()
    );
  }

  public setSidebarTabSelection(sidebarTabAction: SidebarTabAction) {
    this.sidebarTabSelection.next(sidebarTabAction);
  }

  public onDeepLinkParamsChanges<T extends DeeplinkAppParams>() {
    return combineLatest([this.onDefaultAppActionChange$, this.shouldWaitForHiearchy]).pipe(
      map(([onDefaultAppActionChange, shouldWaitForHiearchy]) => {
        if (shouldWaitForHiearchy || !onDefaultAppActionChange) {
          return;
        }
        return onDefaultAppActionChange;
      }),
      filter(
        defaultAppAction =>
          defaultAppAction && defaultAppAction.action === DefaultAppActionType.DeepLinkApp
      ),
      map(defaultAppAction => defaultAppAction.param as DeeplinkParams<T>)
    );
  }

  public dispatchWidgetDate(currentWidgetDate: dcExternalParameter) {
    this.onWidgetDateChange.next(currentWidgetDate);
  }

  public getwidgetDateObservable() {
    return this.widgetDate$;
  }

  public dispatchCreateTask(elementName: string, attachedProcess: string) {
    this.onCreateTask.next({ elementName, attachedProcess });
  }

  public getCreateTaskObservable() {
    return this.task$;
  }

  addLinkedApp(app: LinkedApp) {
    if (!this.linkedApps) {
      this.linkedApps = [];
    }
    this.linkedApps.push(new AppCache(app.Id));
  }
}

export interface CachedAppMenuDetails {
  processName: string;
  appCache: AppCache;
}

export interface AppElementBrowserData {
  //groupDetails: dcGroupDetails[];
  selectedGroupDetail: dcGroupDetails;
  //treeData: dcTagFlatTree[];
  searchScope: dcTag[];
  selectedTag?: dcTag;
  showElementDropdown?: boolean;
}

export interface AppRouteParams {
  appId: string;
  widgetId: string;
}

export interface AppRouteStateParams {}

export interface RouteProcessParamsGeneric<
  CONVERT_TO_NUMBER extends string | number,
  CONVERT_TO_BOOLEAN extends string | boolean
> {
  Id: CONVERT_TO_NUMBER; // process instance id
  ProcessID: CONVERT_TO_NUMBER;
  IsRefresh?: string;
  RefElement?: string;
  RefElementVersionID?: CONVERT_TO_NUMBER;
  ActivityID?: CONVERT_TO_NUMBER;
  forceReload?: CONVERT_TO_BOOLEAN;
  newProcessId?: CONVERT_TO_NUMBER; // new process id
}

//Used when subscribed from routeParams.
export interface RouteProcessParams extends RouteProcessParamsGeneric<string, string> { }

//DTO for process params.
export interface ProcessParams extends RouteProcessParamsGeneric<number, boolean> { }

export class SidebarTags {
  constructor(
    public tags: dcTag[],
    public isDefaultProfileTag?: boolean,
    public appTagTrigger: AppTagTrigger = AppTagTrigger.Default
  ) { }
}

export enum AppTagTrigger {
  Default = 1,
  Deeplink = 2
  //TODO: can implement ElementBrowser, Hierarchy, ScopeTag etc.
}

//add mulitple types here as an when requried .eg. CreateInstaceParam.
export type DefaultAppActionParam<T = DeeplinkParams> = CreateInstanceParam | T;

export enum DefaultAppActionType {
  OpenInSlider,
  OpenElementTag,
  DeepLinkApp
}

export interface DefaultAppAction {
  action: DefaultAppActionType;
  param: DefaultAppActionParam;
}

export interface AppSelectionParams {
  isNewlyOpenedApp?: boolean;
  selected: boolean;
}

export interface DeeplinkAppParams {
  externalParameter?: dcExternalParameter;
}

export interface DeeplinkParams<T = DeeplinkAppParams> {
  tags: dcTag[];
  appParams?: T;
  appCallback?: Subject<AppAction>;
}

export interface SidebarTabAction {
  tabName: string;
  openSidebar?: boolean;
}

export const SAVE_COMPLETED_APPACTION_TYPE = 'SAVE_COMPLETED';
export type AppActionType = 'SAVE_COMPLETED';
//add mulitple types here as an when requried .eg. CreateInstaceParm.
//export type AppActionParam = ;

export interface AppAction {
  action: AppActionType;
  //param: AppActionParam;
}

export interface RequestLogHeader {
  Scope: dcTag[];
  AppID?: number;
  WidgetID?: number;
  ActivityInstanceID?: number;
  ProcessInstanceID?: number;
}

export interface CommonProgressStatus {
  show: boolean;
  showBackDrop: boolean;
}
export interface AppQueryParams { }
export interface DeeplinkAppQueryParams extends AppQueryParams {
  ObjectID: number;
  ElementTypeID: number;
  TagName: string;
  WidgetName: string;
}
